import React, {
  forwardRef,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
// @ts-expect-error - this will be resolved at build time
import { Spinner, Icon } from "@xsolla/xui-primitives";
import { useResolvedTheme, type ThemeOverrideProps } from "@xsolla/xui-core";

export interface FlexButtonProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type">,
    ThemeOverrideProps {
  /**
   * Button label. Omit for icon-only buttons (provide `aria-label`).
   *
   * An icon may also be passed directly as `children` instead of via
   * `iconLeft`/`iconRight`. When `children` carries no text, the button is
   * treated as icon-only and renders as a square sized from the design-system
   * height token for `size`.
   */
  children?: ReactNode;
  /** Visual variant of the button */
  variant?:
    | "brand"
    | "primary"
    | "secondary"
    | "tertiary"
    | "brandExtra"
    | "inverse";
  /** Size of the button */
  size?: "xl" | "lg" | "md" | "sm" | "xs";
  /** Whether to show background fill */
  background?: boolean;
  /**
   * Whether to show the hover/press background color.
   * When `false`, the button background stays transparent in all interactive
   * states, producing a text-only appearance with no hover fill.
   * @default true
   */
  hoverBackground?: boolean;
  /**
   * Remove the button's internal padding so it sits flush against its content.
   * The component applies padding via an inline style, which overrides any CSS
   * class or `style` prop a consumer passes, so this prop is the supported way
   * to render a zero-padding FlexButton (e.g. an inline text action).
   *
   * Icon-only buttons keep their square dimensions when `noPadding` is set —
   * only the inner padding is removed.
   * @default false
   */
  noPadding?: boolean;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Whether the button is in a loading state */
  loading?: boolean;
  /** Icon to display on the left side */
  iconLeft?: ReactNode;
  /** Icon to display on the right side */
  iconRight?: ReactNode;
  /** Click handler */
  onPress?: () => void;
  /** HTML type attribute for the button */
  type?: "button" | "submit" | "reset";
  /** Accessible label for screen readers */
  "aria-label"?: string;
  /** ID of element that describes this button */
  "aria-describedby"?: string;
  /** Indicates the button controls an expandable element */
  "aria-expanded"?: boolean;
  /** Indicates the type of popup triggered by the button */
  "aria-haspopup"?: boolean | "menu" | "listbox" | "tree" | "grid" | "dialog";
  /** Indicates the button is pressed (for toggle buttons) */
  "aria-pressed"?: boolean | "mixed";
  /** ID of the element this button controls */
  "aria-controls"?: string;
  testID?: string;
}

type ButtonState = "default" | "hover" | "press";

/** Icon frame size per size. Mirrors `theme.sizing.flexButton(size).iconSize`. */
const ICON_SIZES: Record<NonNullable<FlexButtonProps["size"]>, number> = {
  xs: 12,
  sm: 14,
  md: 16,
  lg: 18,
  xl: 20,
};

/** Must match {@link ICON_SIZES}, or the button resizes when loading starts. */
const SPINNER_SIZES: Record<NonNullable<FlexButtonProps["size"]>, number> = {
  xs: 12,
  sm: 14,
  md: 16,
  lg: 18,
  xl: 20,
};

const FLEX_BUTTON_PADDING = 4;

/**
 * Whether `node` contributes visible *text* to the button label.
 *
 * React elements are unwrapped one level at a time so an icon element — which
 * renders an `<svg>` and carries no text children — is not mistaken for a text
 * label, while a wrapped label such as `<span>Save</span>` still counts.
 */
const hasVisibleLabel = (node: ReactNode): boolean => {
  if (node == null || node === false || node === true) return false;
  if (typeof node === "string") return node.trim().length > 0;
  if (typeof node === "number") return true;
  if (Array.isArray(node)) return node.some(hasVisibleLabel);
  if (React.isValidElement(node)) {
    const { children: elementChildren } = (node.props ?? {}) as {
      children?: ReactNode;
    };
    return hasVisibleLabel(elementChildren);
  }
  return true;
};

/** Whether `node` renders anything at all (text or element). */
const hasRenderableContent = (node: ReactNode): boolean => {
  if (node == null || node === false || node === true) return false;
  if (typeof node === "string") return node.trim().length > 0;
  if (Array.isArray(node)) return node.some(hasRenderableContent);
  return true;
};

/**
 * Square side of an icon-only `FlexButton`, in px — mirrors
 * `theme.sizing.flexButton(size).height`, which is theme-independent.
 *
 * Duplicated here so layouts that reserve space for a `FlexButton` (e.g. the
 * Modal header columns) can size themselves without a theme lookup. Guarded by
 * a test that compares this table against the token.
 */
const BOX_SIZES: Record<NonNullable<FlexButtonProps["size"]>, number> = {
  xs: 20,
  sm: 22,
  md: 28,
  lg: 32,
  xl: 36,
};

/**
 * Total hit-area size of an icon-only `FlexButton`, in px. `noPadding` does not
 * change it — only the inner padding is removed.
 */
export const getFlexButtonBoxSize = (
  size: NonNullable<FlexButtonProps["size"]> = "md"
): number => BOX_SIZES[size];

const LINE_HEIGHTS: Record<NonNullable<FlexButtonProps["size"]>, string> = {
  xs: "14px",
  sm: "16px",
  md: "18px",
  lg: "20px",
  xl: "22px",
};

const FONT_SIZES: Record<NonNullable<FlexButtonProps["size"]>, number> = {
  xs: 12,
  sm: 14,
  md: 14,
  lg: 16,
  xl: 18,
};

const BORDER_RADIUS: Record<NonNullable<FlexButtonProps["size"]>, number> = {
  xl: 4,
  lg: 4,
  md: 2,
  sm: 2,
  xs: 2,
};

/**
 * FlexButton - A compact button component designed for use in modals and popups.
 *
 * Renders as a semantic `<button>` element with full ARIA support.
 *
 * ## Icon-only buttons
 *
 * A FlexButton is icon-only when it has no text label and an icon is supplied
 * either as `children` or via `iconLeft`/`iconRight`. Icon-only buttons render
 * as a square whose side equals `theme.sizing.flexButton(size).height`, so they
 * line up with the Modal header icon slots (36x36 at `size="xl"`).
 *
 * ```tsx
 * <FlexButton variant="secondary" size="xl" aria-label="Go back">
 *   <BackwardAlt variant="line" />
 * </FlexButton>
 * ```
 *
 * ## Accessibility Features
 *
 * - **Semantic HTML**: Renders as a native `<button>` element
 * - **Keyboard Navigation**: Focusable via Tab, activated with Enter or Space
 * - **ARIA States**: Properly announces disabled and loading states
 * - **Focus Indicator**: Visible focus ring for keyboard navigation
 * - **Screen Reader Support**: Announces button label, state, and any associated descriptions
 */
export const FlexButton = forwardRef<HTMLButtonElement, FlexButtonProps>(
  (
    {
      children,
      variant = "brand",
      size = "md",
      background = false,
      hoverBackground = true,
      noPadding = false,
      disabled = false,
      loading = false,
      iconLeft,
      iconRight,
      onPress,
      onClick,
      className,
      type = "button",
      "aria-label": ariaLabel,
      "aria-describedby": ariaDescribedBy,
      "aria-expanded": ariaExpanded,
      "aria-haspopup": ariaHasPopup,
      "aria-pressed": ariaPressed,
      "aria-controls": ariaControls,
      testID,
      tabIndex = 0,
      themeMode,
      themeProductContext,
      ...buttonProps
    },
    ref
  ) => {
    const { theme } = useResolvedTheme({ themeMode, themeProductContext });
    const [state, setState] = useState<ButtonState>("default");
    const [isFocused, setIsFocused] = useState(false);
    const isMouseOverRef = useRef(false);

    const isDisabled = disabled || loading;

    const getVariantColors = (
      currentState: ButtonState
    ): { bg: string; text: string; border?: string } => {
      if (isDisabled) {
        return {
          bg: background
            ? theme.colors.control.brand.primary.bgDisable
            : "transparent",
          text: theme.colors.control.brand.text.disable,
          border: undefined,
        };
      }

      const effectiveBackground = loading ? false : background;

      // When hoverBackground is disabled, hover/press states use transparent bg.
      switch (variant) {
        case "brand":
          if (effectiveBackground) {
            return {
              bg:
                currentState === "press"
                  ? theme.colors.control.brand.primary.bgPress
                  : currentState === "hover"
                    ? theme.colors.control.brand.primary.bgHover
                    : theme.colors.background.brand.primary,
              text: theme.colors.content.on.brand,
              border: undefined,
            };
          }
          return {
            bg: !hoverBackground
              ? "transparent"
              : currentState === "press"
                ? theme.colors.background.brand.primary
                : currentState === "hover"
                  ? theme.colors.overlay.brand
                  : "transparent",
            text:
              currentState === "press" && hoverBackground
                ? theme.colors.content.on.brand
                : theme.colors.content.brand.primary,
            border: undefined,
          };

        case "primary":
          if (effectiveBackground) {
            return {
              bg: theme.colors.background.primary,
              text: theme.colors.content.primary,
              border:
                currentState === "press"
                  ? theme.colors.border.primary
                  : undefined,
            };
          }
          return {
            bg: !hoverBackground
              ? "transparent"
              : currentState === "press" || currentState === "hover"
                ? theme.colors.overlay.mono
                : "transparent",
            text: theme.colors.content.primary,
            border:
              currentState === "press"
                ? theme.colors.border.primary
                : undefined,
          };

        case "secondary":
          if (effectiveBackground) {
            return {
              bg:
                currentState === "press"
                  ? theme.colors.control.mono.secondary.bgPress
                  : currentState === "hover"
                    ? theme.colors.control.mono.secondary.bgHover
                    : theme.colors.background.secondary,
              text: theme.colors.content.secondary,
              border: undefined,
            };
          }
          return {
            bg: !hoverBackground
              ? "transparent"
              : currentState === "press" || currentState === "hover"
                ? theme.colors.overlay.mono
                : "transparent",
            text:
              currentState === "press"
                ? theme.colors.content.primary
                : currentState === "hover"
                  ? theme.colors.content.secondary
                  : theme.colors.content.secondary,
            border: undefined,
          };

        case "tertiary":
          if (effectiveBackground) {
            return {
              bg:
                currentState === "press"
                  ? theme.colors.control.mono.secondary.bgPress
                  : currentState === "hover"
                    ? theme.colors.control.mono.secondary.bgHover
                    : theme.colors.background.secondary,
              text: theme.colors.content.tertiary,
              border: undefined,
            };
          }
          return {
            bg: !hoverBackground
              ? "transparent"
              : currentState === "press" || currentState === "hover"
                ? theme.colors.overlay.mono
                : "transparent",
            text:
              currentState === "press"
                ? theme.colors.content.secondary
                : currentState === "hover"
                  ? theme.colors.content.tertiary
                  : theme.colors.content.tertiary,
            border: undefined,
          };

        case "brandExtra":
          if (effectiveBackground) {
            return {
              bg:
                currentState === "press"
                  ? theme.colors.control.brandExtra.primary.bgPress
                  : currentState === "hover"
                    ? theme.colors.control.brandExtra.primary.bgHover
                    : theme.colors.background.brandExtra.primary,
              text: theme.colors.content.on.brandExtra,
              border: undefined,
            };
          }
          return {
            bg: !hoverBackground
              ? "transparent"
              : currentState === "press"
                ? theme.colors.background.brandExtra.primary
                : currentState === "hover"
                  ? theme.colors.overlay.brandExtra
                  : "transparent",
            text:
              currentState === "press"
                ? theme.colors.content.on.brandExtra
                : theme.colors.content.brandExtra.secondary,
            border: undefined,
          };

        case "inverse":
          if (effectiveBackground) {
            return {
              bg:
                currentState === "press"
                  ? theme.colors.control.mono.primary.bgPress
                  : currentState === "hover"
                    ? theme.colors.control.mono.primary.bgHover
                    : theme.colors.background.inverse,
              text: theme.colors.content.inverse,
              border: undefined,
            };
          }
          return {
            bg: !hoverBackground
              ? "transparent"
              : currentState === "press" || currentState === "hover"
                ? theme.colors.overlay.mono
                : "transparent",
            text: theme.colors.content.inverse,
            border: undefined,
          };

        default:
          return {
            bg: "transparent",
            text: theme.colors.content.primary,
            border: undefined,
          };
      }
    };

    const getFocusRingColor = (): string => {
      switch (variant) {
        case "brand":
          return theme.colors.overlay.brand;
        case "brandExtra":
          return theme.colors.overlay.brandExtra;
        case "inverse":
          return "rgba(255, 255, 255, 0.3)";
        default:
          return theme.colors.overlay.mono;
      }
    };

    const getSpinnerColor = (): string => {
      switch (variant) {
        case "brand":
          return theme.colors.content.brand.primary;
        case "primary":
          return theme.colors.content.primary;
        case "secondary":
          return theme.colors.content.secondary;
        case "tertiary":
          return theme.colors.content.tertiary;
        case "brandExtra":
          return theme.colors.content.brandExtra.secondary;
        case "inverse":
          return theme.colors.content.inverse;
        default:
          return theme.colors.content.brand.primary;
      }
    };

    const colors = getVariantColors(state);
    const focusRingColor = getFocusRingColor();
    const spinnerColor = getSpinnerColor();
    const iconSize = ICON_SIZES[size];
    const spinnerSize = SPINNER_SIZES[size];
    const fontSize = FONT_SIZES[size];
    const borderRadius = BORDER_RADIUS[size];
    const lineHeight = LINE_HEIGHTS[size];
    const showTextLabel = hasVisibleLabel(children);
    // An icon passed as `children` (rather than via iconLeft/iconRight) is still
    // icon content, so it must not fall through to the text-rendering path.
    const hasIconChildren = !showTextLabel && hasRenderableContent(children);
    const isIconOnly =
      !loading &&
      !showTextLabel &&
      Boolean(iconLeft || iconRight || hasIconChildren);
    // Square hit area comes from the design-system height token so an icon-only
    // FlexButton matches the Modal header icon slots (36px at `xl`) instead of
    // collapsing to `iconSize + padding`.
    const iconOnlyHitArea = getFlexButtonBoxSize(size);

    const handleMouseEnter = () => {
      if (!isDisabled) {
        isMouseOverRef.current = true;
        setState("hover");
      }
    };

    const handleMouseLeave = () => {
      if (!isDisabled) {
        isMouseOverRef.current = false;
        setState("default");
      }
    };

    const handleMouseDown = () => {
      if (!isDisabled) {
        setState("press");
      }
    };

    const handleMouseUp = () => {
      if (!isDisabled) {
        setState(isMouseOverRef.current ? "hover" : "default");
      }
    };

    const handleFocus = () => {
      if (!isDisabled) {
        setIsFocused(true);
      }
    };

    const handleBlur = () => {
      setIsFocused(false);
    };

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (isDisabled) return;
      if (onPress) {
        onPress();
      }
      if (onClick) {
        onClick(event);
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (isDisabled) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        setState("press");
      }
    };

    const handleKeyUp = (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (isDisabled) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        setState(isMouseOverRef.current ? "hover" : "default");
        if (onPress) {
          onPress();
        }
      }
    };

    const borderShadow = colors.border
      ? `inset 0 0 0 1px ${colors.border}`
      : undefined;
    const focusShadow =
      isFocused && !isDisabled ? `0 0 0 2px ${focusRingColor}` : undefined;

    const boxShadows: string[] = [];
    if (borderShadow) boxShadows.push(borderShadow);
    if (focusShadow) boxShadows.push(focusShadow);
    const combinedBoxShadow =
      boxShadows.length > 0 ? boxShadows.join(", ") : "none";

    // No whole-element opacity for the disabled state: the disabled palette is
    // already flat and deliberate (`control.brand.primary.bgDisable` /
    // transparent fill plus `control.brand.text.disable` label), and layering
    // opacity on top composited it against the host surface — a solid token
    // rendered translucent in consuming apps while looking correct in
    // Storybook. See FEP-828.
    const buttonStyle: CSSProperties = {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: isIconOnly ? 0 : "2px",
      boxSizing: "border-box",
      padding: noPadding ? "0px" : `${FLEX_BUTTON_PADDING}px`,
      backgroundColor: loading ? "transparent" : colors.bg,
      color: colors.text,
      border: "none",
      borderWidth: "0px",
      borderRadius: `${borderRadius}px`,
      cursor: isDisabled ? "not-allowed" : "pointer",
      fontSize: `${fontSize}px`,
      fontWeight: 500,
      lineHeight: isIconOnly ? 0 : lineHeight,
      fontFamily: "inherit",
      transition:
        "background-color 100ms ease-in-out, color 100ms ease-in-out, box-shadow 100ms ease-in-out",
      outline: "none",
      boxShadow: combinedBoxShadow,
      ...(isIconOnly && {
        width: iconOnlyHitArea,
        height: iconOnlyHitArea,
        minWidth: iconOnlyHitArea,
        minHeight: iconOnlyHitArea,
        flexShrink: 0,
      }),
    };

    const contentStyle: CSSProperties = {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: isIconOnly ? 0 : "2px",
      ...(isIconOnly && {
        width: "100%",
        height: "100%",
        lineHeight: 0,
      }),
    };

    const spinnerStyle: CSSProperties = {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "transparent",
      height: lineHeight,
    };

    const computedAriaLabel =
      ariaLabel || (typeof children === "string" ? children : undefined);

    return (
      <button
        {...buttonProps}
        ref={ref}
        type={type}
        className={className}
        disabled={isDisabled}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onFocus={handleFocus}
        onBlur={handleBlur}
        aria-label={computedAriaLabel}
        aria-busy={loading || undefined}
        aria-disabled={isDisabled || undefined}
        aria-describedby={ariaDescribedBy}
        aria-expanded={ariaExpanded}
        aria-haspopup={ariaHasPopup}
        aria-pressed={ariaPressed}
        aria-controls={ariaControls}
        tabIndex={tabIndex}
        style={buttonStyle}
        data-testid={testID || "flex-button"}
      >
        <span style={contentStyle}>
          {loading ? (
            <span style={spinnerStyle}>
              <Spinner size={spinnerSize} color={spinnerColor} />
            </span>
          ) : (
            <>
              {iconLeft && (
                <Icon size={iconSize} color={colors.text}>
                  {iconLeft}
                </Icon>
              )}
              {showTextLabel && <span>{children}</span>}
              {hasIconChildren && (
                <Icon size={iconSize} color={colors.text}>
                  {children}
                </Icon>
              )}
              {iconRight && (
                <Icon size={iconSize} color={colors.text}>
                  {iconRight}
                </Icon>
              )}
            </>
          )}
        </span>
      </button>
    );
  }
);

FlexButton.displayName = "FlexButton";
